/**
 * Role-aware landing and visibility canon.
 * Frontend visibility ≠ backend authority.
 */

import type { ExperienceAreaId } from './primaryNavigationCanon';
import {
  ADVANCED_NAVIGATION_LAYERS_V0,
  ALL_NAVIGATION_AREAS_V0,
  PRIMARY_NAVIGATION_AREAS_V0,
} from './primaryNavigationCanon';

export type ExperienceRoleId =
  | 'executive'
  | 'manager'
  | 'operator'
  | 'field_worker'
  | 'approver'
  | 'auditor'
  | 'tenant_admin'
  | 'external_customer'
  | 'external_supplier'
  | 'system_operator';

export type AreaVisibility = 'visible' | 'hidden' | 'read_only';

export interface RoleNavigationProfile {
  roleId: ExperienceRoleId;
  defaultLanding: string;
  visiblePrimaryAreas: readonly ExperienceAreaId[];
  hiddenAreas: readonly ExperienceAreaId[];
  readOnlyAreas: readonly ExperienceAreaId[];
  adminLayerAccess: boolean;
  consoleAccess: boolean;
  mobileDefaultLanding: string;
  notes?: string;
}

const ALL_PRIMARY_IDS = PRIMARY_NAVIGATION_AREAS_V0.map((a) => a.areaId);

function primaryExcept(...hidden: ExperienceAreaId[]): ExperienceAreaId[] {
  const hide = new Set(hidden);
  return ALL_PRIMARY_IDS.filter((id) => !hide.has(id));
}

export const ROLE_NAVIGATION_MATRIX_V0: readonly RoleNavigationProfile[] = [
  {
    roleId: 'executive',
    defaultLanding: '/workspace',
    visiblePrimaryAreas: primaryExcept('automation'),
    hiddenAreas: [],
    readOnlyAreas: ['operations', 'objects', 'automation'],
    adminLayerAccess: false,
    consoleAccess: false,
    mobileDefaultLanding: '/workspace',
    notes: 'Risk / delayed / decisions / exceptions triage.',
  },
  {
    roleId: 'manager',
    defaultLanding: '/workspace',
    visiblePrimaryAreas: ALL_PRIMARY_IDS,
    hiddenAreas: [],
    readOnlyAreas: [],
    adminLayerAccess: false,
    consoleAccess: false,
    mobileDefaultLanding: '/workspace/assignments',
  },
  {
    roleId: 'operator',
    defaultLanding: '/workspace/tasks',
    visiblePrimaryAreas: primaryExcept(),
    hiddenAreas: [],
    readOnlyAreas: [],
    adminLayerAccess: false,
    consoleAccess: false,
    mobileDefaultLanding: '/workspace/tasks',
  },
  {
    roleId: 'field_worker',
    defaultLanding: '/workspace/tasks',
    visiblePrimaryAreas: ['today', 'workbench', 'operations', 'objects', 'evidence_decisions'],
    hiddenAreas: ['people_authority', 'automation', 'configure', 'operations_console'],
    readOnlyAreas: [],
    adminLayerAccess: false,
    consoleAccess: false,
    mobileDefaultLanding: '/workspace/tasks',
    notes: 'Mobile: task / scan / evidence / recovery.',
  },
  {
    roleId: 'approver',
    defaultLanding: '/approvals/pending',
    visiblePrimaryAreas: ['today', 'workbench', 'operations', 'evidence_decisions', 'objects'],
    hiddenAreas: ['automation', 'configure', 'operations_console'],
    readOnlyAreas: ['operations', 'objects'],
    adminLayerAccess: false,
    consoleAccess: false,
    mobileDefaultLanding: '/approvals/pending',
  },
  {
    roleId: 'auditor',
    defaultLanding: '/approvals/history',
    visiblePrimaryAreas: [
      'today',
      'operations',
      'objects',
      'evidence_decisions',
      'people_authority',
    ],
    hiddenAreas: ['workbench', 'automation', 'configure'],
    readOnlyAreas: ['today', 'operations', 'objects', 'evidence_decisions', 'people_authority'],
    adminLayerAccess: false,
    consoleAccess: true,
    mobileDefaultLanding: '/approvals/history',
  },
  {
    roleId: 'tenant_admin',
    defaultLanding: '/workspace',
    visiblePrimaryAreas: ALL_PRIMARY_IDS,
    hiddenAreas: [],
    readOnlyAreas: [],
    adminLayerAccess: true,
    consoleAccess: true,
    mobileDefaultLanding: '/workspace',
  },
  {
    roleId: 'external_customer',
    defaultLanding: '/portal',
    visiblePrimaryAreas: [],
    hiddenAreas: ALL_PRIMARY_IDS.concat(['configure', 'operations_console']),
    readOnlyAreas: [],
    adminLayerAccess: false,
    consoleAccess: false,
    mobileDefaultLanding: '/portal',
    notes: 'Relationship plane — business primary nav not exposed.',
  },
  {
    roleId: 'external_supplier',
    defaultLanding: '/portal',
    visiblePrimaryAreas: [],
    hiddenAreas: ALL_PRIMARY_IDS.concat(['configure', 'operations_console']),
    readOnlyAreas: [],
    adminLayerAccess: false,
    consoleAccess: false,
    mobileDefaultLanding: '/portal',
    notes: 'Relationship plane — business primary nav not exposed.',
  },
  {
    roleId: 'system_operator',
    defaultLanding: '/conscious',
    visiblePrimaryAreas: ['today', 'operations', 'evidence_decisions', 'automation'],
    hiddenAreas: [],
    readOnlyAreas: [],
    adminLayerAccess: true,
    consoleAccess: true,
    mobileDefaultLanding: '/conscious/alerts',
  },
] as const;

export const FRONTEND_VISIBILITY_IS_NOT_AUTHORITY_V0 = true as const;

export function getRoleNavigationProfile(roleId: ExperienceRoleId): RoleNavigationProfile {
  const found = ROLE_NAVIGATION_MATRIX_V0.find((row) => row.roleId === roleId);
  if (!found) {
    throw new Error(`Unknown experience role: ${roleId}`);
  }
  return found;
}

export function resolveRoleDefaultLanding(roleId: ExperienceRoleId): string {
  return getRoleNavigationProfile(roleId).defaultLanding;
}

export function isAreaVisibleForRole(roleId: ExperienceRoleId, areaId: ExperienceAreaId): boolean {
  const profile = getRoleNavigationProfile(roleId);
  if (profile.hiddenAreas.includes(areaId)) return false;
  if (areaId === 'configure') return profile.adminLayerAccess;
  if (areaId === 'operations_console') return profile.consoleAccess;
  return profile.visiblePrimaryAreas.includes(areaId);
}

export function filterAreasForRole(roleId: ExperienceRoleId): {
  primary: ExperienceAreaId[];
  advanced: ExperienceAreaId[];
} {
  const primary = PRIMARY_NAVIGATION_AREAS_V0.map((a) => a.areaId).filter((id) =>
    isAreaVisibleForRole(roleId, id)
  );
  const advanced = ADVANCED_NAVIGATION_LAYERS_V0.map((a) => a.areaId).filter((id) =>
    isAreaVisibleForRole(roleId, id)
  );
  return { primary, advanced };
}

/** Reverse lookup: which frozen area (if any) owns this exact default-landing path. */
function findAreaIdForDefaultLandingPath(path: string): ExperienceAreaId | undefined {
  return ALL_NAVIGATION_AREAS_V0.find((area) => area.defaultLanding === path)?.areaId;
}

/**
 * Runtime landing resolver — the only intended production entrypoint for
 * turning a resolved `ExperienceRoleId` into a redirect target (N1 role
 * landing activation).
 *
 * Fail-closed contract:
 * - `roleId === null` (unresolved actor — no ambiguous inference, no invented
 *   role) → always returns `fallbackPath` unchanged. This must be the same
 *   conservative default the router used before role-matrix wiring existed,
 *   so an unresolved actor never sees a *different* (and potentially more
 *   privileged) landing than pre-activation behavior.
 * - `roleId` resolved → looks up `getRoleNavigationProfile(roleId)` and
 *   additionally *cross-checks* `filterAreasForRole(roleId)`: if the area
 *   that owns the profile's `defaultLanding` is not in that same role's
 *   visible (primary ∪ advanced) area set, the matrix row is internally
 *   inconsistent (an authoring bug in the frozen matrix, not a runtime
 *   condition), and we fail closed to `fallbackPath` rather than trust a
 *   landing that contradicts the role's own declared visibility — this is
 *   the concrete consumption of `filterAreasForRole` this function exists
 *   to prove, not a decorative call.
 * - A `defaultLanding` with no owning area (e.g. a role-specific path not
 *   modeled as one of the 9 frozen areas, such as `/approvals/pending`)
 *   skips the cross-check and is trusted directly — absence of an area
 *   mapping is not evidence of inconsistency.
 */
export function resolveRoleAwareLandingPath(
  roleId: ExperienceRoleId | null,
  fallbackPath: string
): string {
  if (!roleId) {
    return fallbackPath;
  }
  const profile = getRoleNavigationProfile(roleId);
  const ownerAreaId = findAreaIdForDefaultLandingPath(profile.defaultLanding);
  if (ownerAreaId) {
    const { primary, advanced } = filterAreasForRole(roleId);
    const visible = new Set<ExperienceAreaId>([...primary, ...advanced]);
    if (!visible.has(ownerAreaId)) {
      return fallbackPath;
    }
  }
  return profile.defaultLanding;
}
